'use client'

/**
 * Los dos enlaces medidos del artículo de guía: el CTA de cierre y el enlace
 * bajo la entradilla.
 *
 * Existen como client components solo para medir el click: `GuideArticle` es
 * server component y sin esto serían caminos sin instrumentar, así que no se
 * podrían comparar contra el CTA del bloque de preview.
 *
 * Son dos componentes y no uno con una prop de variante porque difieren en las
 * dos cosas a la vez —forma y `placement` medido—, y un booleano que cambie
 * ambas es justo lo que hace ilegible el sitio de uso.
 */

import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { captureProductEvent, PRODUCT_EVENTS } from '@/lib/product-analytics'
import type { GuideCharacterKey, GuideHeroCtaDestination, GuideLocale } from '@/lib/guides'

type GuideCtaProps = {
  href: string
  label: string
  className: string
  character: GuideCharacterKey
  locale: GuideLocale
  guideSlug: string
}

/**
 * `placement` distingue los dos sitios para poder compararlos en PostHog;
 * `destination` dice adónde llevó el enlace bajo la entradilla (la landing del
 * personaje, su acceso o la landing «Papá Noel gratis»), para que un click
 * hacia lo gratis no se cuente como intención de llamar.
 */
function trackClick(
  { character, guideSlug, locale }: GuideCtaProps,
  placement: 'article_footer' | 'hero',
  destination?: GuideHeroCtaDestination
) {
  captureProductEvent(PRODUCT_EVENTS.GUIDE_CTA_CLICKED, {
    character_id: character,
    guide_slug: guideSlug,
    locale,
    placement,
    ...(destination && { destination }),
  })
}

/** CTA de cierre del artículo (`CtaBlock`): botón. */
export function GuideCtaLink(props: GuideCtaProps) {
  return (
    <Button size="lg" asChild className={props.className}>
      <Link href={props.href} onClick={() => trackClick(props, 'article_footer')}>
        {props.label}
      </Link>
    </Button>
  )
}

/**
 * Enlace bajo la entradilla. Su destino y su forma (`emphasis`: texto o botón)
 * salen de la intención editorial de la guía (`Guide.heroCta`); el destino
 * viaja en el evento y la forma en `data-emphasis`.
 *
 * Nada de `<Button asChild>` aquí: sus clases traen `inline-flex` y sin salto
 * de línea, así que una frase entera se sale del móvil (519 px medidos en un
 * viewport de 390). Es una frase dentro de un párrafo y tiene que fluir como
 * tal.
 */
export function GuideHeroCtaLink(
  props: GuideCtaProps & { destination: GuideHeroCtaDestination; emphasis: 'button' | 'link' }
) {
  return (
    <Link
      href={props.href}
      onClick={() => trackClick(props, 'hero', props.destination)}
      className={props.className}
      data-emphasis={props.emphasis}
    >
      {props.label}
    </Link>
  )
}
